import type { Command, CommandProvider, CommandContext, SearchProvider } from './types';
import { useUsageStore } from './usage-store';

/**
 * Central registry for the command bar. Providers register here once at
 * module load time; `listCommands(ctx)` materializes the current list and
 * filters by `when()` guards. A thin `run()` wrapper logs usage.
 */
const commandProviders: CommandProvider[] = [];
const searchProviders: SearchProvider[] = [];

export function registerCommands(provider: CommandProvider): void {
  commandProviders.push(provider);
}

export function registerSearchProvider(provider: SearchProvider): void {
  searchProviders.push(provider);
}

/** Materialize the current command list, applying `when()` guards. */
export function listCommands(ctx: CommandContext): Command[] {
  const out: Command[] = [];
  for (const provider of commandProviders) {
    const cmds = typeof provider === 'function' ? provider(ctx) : provider;
    for (const cmd of cmds) {
      if (cmd.when && !cmd.when(ctx)) continue;
      out.push(cmd);
    }
  }
  return out;
}

/** Raw access to the registered search providers — consumers rank per-scope. */
export function listSearchProviders(): SearchProvider[] {
  return searchProviders;
}

/**
 * Invoke a command and record the usage hit. Async errors bubble — the bar
 * closes before `run()` so toasts surface whatever went wrong.
 */
export async function runCommand(cmd: Command, ctx: CommandContext): Promise<void> {
  useUsageStore.getState().recordCommand(cmd.id);
  await cmd.run(ctx);
}

/** Invoke a picked search item and record the usage hit. */
export async function runSearchItem(
  item: import('./types').SearchItem,
  ctx: CommandContext,
): Promise<void> {
  useUsageStore.getState().recordSearch(item.id);
  await item.action(ctx);
}

/** Count registered providers — useful for debugging an empty bar. */
export function _providerCounts(): { commands: number; search: number } {
  return { commands: commandProviders.length, search: searchProviders.length };
}
